import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Search, GraduationCap, Loader2, TriangleAlert } from "lucide-react";
import { Label } from "../ui/label";
import {
  getCoteachers,
  searchCoteacherCandidates,
  searchOrgTeacherCandidates,
  updateCoteachers,
  coteachingErrorMessage,
  type CoTeacher,
} from "./coteaching-api";

type CoTeachingFieldProps = {
  teacherId: number;
  organizationId?: string;
  initialOrganizationId?: string;
  onCountChange?: (count: number) => void;
};

export const CoTeachingField = ({
  teacherId,
  organizationId,
  initialOrganizationId,
  onCountChange,
}: CoTeachingFieldProps) => {
  const [coTeachers, setCoTeachers] = useState<CoTeacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CoTeacher[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [pendingRemove, setPendingRemove] = useState<CoTeacher | null>(null);
  const linkedOrgRef = useRef(initialOrganizationId);
  const coTeachersRef = useRef<CoTeacher[]>([]);
  coTeachersRef.current = coTeachers;

  useEffect(() => {
    let cancelled = false;
    getCoteachers(teacherId)
      .then((data) => {
        if (!cancelled) {
          setCoTeachers(data.coteachers);
          onCountChange?.(data.coteachers.length);
        }
      })
      .catch((e) => {
        if (!cancelled)
          setLoadError(coteachingErrorMessage(e, "Failed to load co-teachers"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherId]);

  useEffect(() => {
    if (loading) return;
    if (organizationId === linkedOrgRef.current) return;
    linkedOrgRef.current = organizationId;
    setPicking(false);
    setQuery("");
    const handle = setTimeout(() => {
      const current = coTeachersRef.current;
      if (current.length > 0) {
        void persist([], current);
      }
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, loading]);

  const orgChangePending =
    initialOrganizationId !== undefined &&
    organizationId !== initialOrganizationId;

  useEffect(() => {
    if (!picking) return;
    let cancelled = false;
    setSearching(true);
    setSearchError("");
    const handle = setTimeout(() => {
      const search = organizationId
        ? searchOrgTeacherCandidates(organizationId, query.trim())
        : searchCoteacherCandidates(teacherId, query.trim());
      search
        .then((found) => {
          if (cancelled) return;
          const linked = new Set(coTeachers.map((c) => c.teacher_id));
          setResults(
            found.filter(
              (t) => t.teacher_id !== teacherId && !linked.has(t.teacher_id),
            ),
          );
        })
        .catch((e) => {
          if (!cancelled)
            setSearchError(coteachingErrorMessage(e, "Search failed"));
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [picking, query, teacherId, organizationId, coTeachers]);

  const persist = async (next: CoTeacher[], prev: CoTeacher[]) => {
    setCoTeachers(next);
    setSaving(true);
    try {
      const updated = await updateCoteachers(
        teacherId,
        next.map((c) => c.teacher_id),
      );
      setCoTeachers(updated.coteachers);
      onCountChange?.(updated.coteachers.length);
    } catch (e) {
      setCoTeachers(prev);
      toast.error(coteachingErrorMessage(e, "Failed to update co-teachers"));
    } finally {
      setSaving(false);
    }
  };

  const addCoTeacher = (teacher: CoTeacher) => {
    setPicking(false);
    setQuery("");
    void persist([...coTeachers, teacher], coTeachers);
  };

  const confirmRemove = () => {
    if (!pendingRemove) return;
    const target = pendingRemove;
    setPendingRemove(null);
    void persist(
      coTeachers.filter((c) => c.teacher_id !== target.teacher_id),
      coTeachers,
    );
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label>Co-teaching with</Label>
          <span className="text-xs text-muted-foreground">
            {saving ? "Saving..." : "Teacher accounts"}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          These teachers can co-teach in this account. One-directional: they
          don't see yours.
        </p>
      </div>

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading co-teachers...
        </p>
      ) : loadError ? (
        <p className="text-sm text-destructive">{loadError}</p>
      ) : (
        <>
          {coTeachers.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              {coTeachers.map((c) => (
                <div
                  key={c.teacher_id}
                  className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-lime-600/10 text-xs font-medium text-lime-900">
                      {(c.name ?? c.email ?? "?").charAt(0).toUpperCase()}
                    </span>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-foreground">
                        {c.name ?? "(no name)"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {c.email}
                      </span>
                    </div>
                    <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      <GraduationCap className="h-3 w-3" />
                      Teacher
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={saving || orgChangePending}
                    aria-label={`Remove ${c.name ?? c.email}`}
                    onClick={() => setPendingRemove(c)}
                    className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              No co-teaching links yet.
            </p>
          )}

          {picking && !orgChangePending ? (
            <div className="rounded-md border border-border/60 bg-background shadow-sm">
              <div className="flex items-center gap-2 border-b border-border/60 px-2.5">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  autoFocus
                  className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  placeholder="Search teacher by name or email..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <button
                  type="button"
                  aria-label="Close search"
                  onClick={() => {
                    setPicking(false);
                    setQuery("");
                  }}
                  className="-mr-1 shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="max-h-44 overflow-y-auto p-1">
                {searching ? (
                  <p className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Searching...
                  </p>
                ) : searchError ? (
                  <p className="px-2 py-1.5 text-sm text-destructive">
                    {searchError}
                  </p>
                ) : results.length === 0 ? (
                  <p className="px-2 py-1.5 text-sm text-muted-foreground">
                    No matching teacher accounts in this organization.
                  </p>
                ) : (
                  results.map((t) => (
                    <button
                      key={t.teacher_id}
                      type="button"
                      onClick={() => addCoTeacher(t)}
                      className="flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-accent"
                    >
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-lime-600/10 text-xs font-medium text-lime-900">
                        {(t.name ?? t.email ?? "?").charAt(0).toUpperCase()}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-medium text-foreground">
                          {t.name ?? "(no name)"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t.email}
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <button
              type="button"
              disabled={saving || orgChangePending}
              onClick={() => setPicking(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border/60 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              Add co-teacher
            </button>
          )}
        </>
      )}

      {pendingRemove ? (
        <RemoveConfirm
          name={pendingRemove.name ?? pendingRemove.email ?? "this teacher"}
          onCancel={() => setPendingRemove(null)}
          onConfirm={confirmRemove}
        />
      ) : null}
    </div>
  );
};

const RemoveConfirm = ({
  name,
  onCancel,
  onConfirm,
}: {
  name: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div
        role="alertdialog"
        className="relative z-[61] w-full max-w-sm rounded-lg border bg-background p-6 shadow-lg"
      >
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <TriangleAlert className="size-5" />
          </span>
          <div className="flex flex-col gap-1">
            <h3 className="text-base font-semibold text-foreground">
              Remove co-teacher?
            </h3>
            <p className="text-sm text-muted-foreground">
              Remove <span className="font-medium text-foreground">{name}</span>{" "}
              as a co-teacher? This takes effect immediately.
            </p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md bg-destructive px-3 text-sm font-medium text-white transition-colors hover:bg-destructive/90"
          >
            <X className="h-4 w-4" />
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};
